import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Badge, Inline } from 'braid-design-system';
import { type ComponentProps, Fragment, type FC } from 'react';

import { PriorityApplyBadge } from 'src/App/AppPageBlock/PriorityApplyBadge';
import { VerifiedCredentialsBadge } from 'src/App/AppPageBlock/SEEKPass/VerifiedCredentialsBadge';
import { NewBadge } from 'src/components/common/NewBadge';
import { Display } from 'src/libs/Display';
import type { BUCKETS } from 'src/stores/JobApplicationStore/types';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import { HighFitBadgeWithTooltip } from '../HighFitBadge';
import { ReferenceChecksBadge } from '../ReferenceChecksBadge';
import { RelevantApplicationBadge } from '../RelevantApplicationBadge';

import translations from './.vocab';

export interface ApplicationBadgesInOrderProps {
  shouldShowNewBadge: boolean;
  shouldShowHighFitBadge: boolean;
  shouldShowRelevantApplicationBadge: boolean;
  shouldShowPriorityApplyBadge: boolean;
  referenceChecks: ComponentProps<typeof ReferenceChecksBadge> | null;
  verificationsCount: number;
  verifications: string[];
  jobApplicationId: string;
  applicationStatus: BUCKETS;
  applicationName: string;
  language: Language;
}

export const ApplicationBadgesInOrder: FC<ApplicationBadgesInOrderProps> = ({
  shouldShowNewBadge,
  shouldShowHighFitBadge,
  referenceChecks,
  shouldShowRelevantApplicationBadge,
  shouldShowPriorityApplyBadge,
  verificationsCount,
  verifications,
  jobApplicationId,
  applicationStatus,
  applicationName,
  language,
}) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();

  const badges = [];

  if (referenceChecks) {
    const badge = <ReferenceChecksBadge {...referenceChecks} />;

    switch (referenceChecks.case) {
      case 'REQUESTED': {
        badges.push({
          name: t('Checking references'),
          badge,
        });
        break;
      }
      case 'LIMITED_AVAILABLE': {
        badges.push({
          name: t('Limited reference/s available ({count})', {
            count: referenceChecks.counts.finishedCount,
          }),
          badge,
        });
        break;
      }
      case 'STANDARD_AVAILABLE': {
        badges.push({
          name: t('Reference/s available ({count})', {
            count: referenceChecks.counts.finishedCount,
          }),
          badge,
        });
        break;
      }
    }
  }

  if (shouldShowRelevantApplicationBadge) {
    badges.push({
      name: t('Relevant application'),
      badge: (
        <RelevantApplicationBadge
          idSuffix={jobApplicationId}
          shouldShowTopPlacement={true}
        />
      ),
    });
  }

  if (verificationsCount > 0) {
    badges.push({
      name: t('Verified credentials'),
      badge: (
        <VerifiedCredentialsBadge
          idSuffix={jobApplicationId}
          verificationsCount={verificationsCount}
          verifications={verifications}
        />
      ),
    });
  }

  if (shouldShowPriorityApplyBadge) {
    badges.push({
      name: t('Highly interested'),
      badge: <PriorityApplyBadge variant="badge" />,
    });
  }

  if (shouldShowHighFitBadge) {
    badges.push({
      name: t('High fit'),
      badge: (
        <Display mobile tablet>
          <HighFitBadgeWithTooltip
            variant="badge"
            applicantName={applicationName}
          />
        </Display>
      ),
    });
  }

  return (
    <Inline space="xsmall" alignY="center">
      {shouldShowNewBadge ? <NewBadge /> : null}

      {renderBadgesAlphabetically(badges, language)}

      <Badge tone="neutral">{getLabelFromBucket(applicationStatus)}</Badge>
    </Inline>
  );
};

const renderBadgesAlphabetically = (
  badges: Array<{ name: string; badge: JSX.Element }>,
  language: Language,
) =>
  badges
    .sort((a, b) => a.name.localeCompare(b.name, language))
    .map(({ name, badge }) => <Fragment key={name}>{badge}</Fragment>);
